'use strict';

/**
 * T372 INTEGRITY-HARDENING follow-on — authenticity vs a compromised server.
 *
 * HONEST DOCTRINE (LD-INTEGRITY-DOCTRINE): the sealed-box MVP delivers at-rest
 * CONFIDENTIALITY, a strict improvement over today. This module adds
 * tamper-EVIDENCE and recipient-key binding — it RAISES the bar against a
 * server-write attacker; it does NOT deliver full authenticity, and it does
 * NOT fully close the recipient-key swap. The full closure (anchoring the SIGN
 * key to a server-unwriteable root) is P2b-ANCHOR / a documented residual —
 * the SIGN public key ALSO lives in server-mutable meta.publicKeys with no
 * in-code immutable anchor today. Do NOT over-claim.
 *
 *   - signRecipientKex / verifyRecipientKex: the client self-signs publicKeys
 *     .kex with the vault SIGN keypair (ed25519 + ML-DSA hybrid); the server
 *     verifies BEFORE sealing. A swapped/unsigned kex is REFUSED. (LD-RECIPIENT
 *     -KEY-BINDING step 1.)
 *   - verifyChain: the client verifies the seq + prevHash backward chain on
 *     read — detects REORDER / MIDDLE-DROP / DUPLICATE, NOT tail-truncation
 *     (a shorter chain stays internally valid). Per-day-file genesis. With a
 *     false-positive guard for legit gaps. (LD-TAMPER-CHAIN.)
 *   - verifyChainAgainstHwm: tail-truncation detection against a client
 *     -persisted high-water-mark (last-seen seq + count). (LD-TAMPER-CHAIN.)
 */

const hybridSign = require('./hybrid-sign');
const sealBox = require('./seal-box');

// Canonical bytes the recipient-key self-signature covers: the kex public key
// material in a PINNED layout so a signature cannot be replayed across a
// different kex. (Mirrors the AAD-canonicalization discipline of seal-box.)
function recipientKexCanonicalBytes(pubKex) {
  if (!pubKex || !pubKex.x25519 || !pubKex.mlKem) throw new Error('seal-box-integrity: invalid pubKex');
  const toU8 = (v) => {
    if (v instanceof Uint8Array) return v;
    if (v && typeof v === 'object' && typeof v.__u8 === 'string') return new Uint8Array(Buffer.from(v.__u8, 'hex'));
    if (typeof v === 'string') return new Uint8Array(Buffer.from(v, 'hex'));
    if (Array.isArray(v)) return Uint8Array.from(v);
    throw new Error('seal-box-integrity: unrecognized kex encoding');
  };
  const label = new TextEncoder().encode('oyster:recipient-kex-binding:v1');
  const x = toU8(pubKex.x25519);
  const m = toU8(pubKex.mlKem);
  const out = new Uint8Array(label.length + x.length + m.length);
  out.set(label, 0);
  out.set(x, label.length);
  out.set(m, label.length + x.length);
  return out;
}

/**
 * Self-sign a recipient kex public key with the vault SIGN private key.
 * @returns the signature (hybrid ed25519 + ML-DSA), as produced by hybrid-sign.
 */
function signRecipientKex(pubKex, signPrivateKey) {
  const msg = recipientKexCanonicalBytes(pubKex);
  return hybridSign.sign(signPrivateKey, msg);
}

/**
 * Verify a recipient kex self-signature against the bound SIGN public key.
 * Returns false (never throws) for an unsigned / bad-signature / swapped kex —
 * so the SERVER can REFUSE to seal to an unverified recipient.
 */
function verifyRecipientKex(pubKex, signature, signPublicKey) {
  if (!signature || !signPublicKey) return false;
  try {
    const msg = recipientKexCanonicalBytes(pubKex);
    return hybridSign.verify(signPublicKey, msg, signature) === true;
  } catch {
    return false;
  }
}

/**
 * Verify the seq + prevHash backward chain of a list of cbox-v1 envelopes
 * (already in file order, oldest→newest), per-day-file.
 *
 * Detects: REORDER, MIDDLE-DROP, DUPLICATE. Does NOT detect tail-truncation
 * (use verifyChainAgainstHwm). Unknown/non-cbox lines are ignored by the
 * caller (the reader drops them, LD-7) so this sees only cbox-v1 envelopes.
 *
 * @returns {{ok:boolean, tampered:boolean, reason:(string|null), at:(number|null)}}
 */
function verifyChain(envelopes) {
  const list = Array.isArray(envelopes) ? envelopes.filter(e => e && e.seal === sealBox.SEAL_MARKER) : [];
  if (list.length === 0) return { ok: true, tampered: false, reason: null, at: null };

  let expectedSeq = list[0].seq;
  let prevHash = list[0].prevHash;
  // The chain must root at the per-file genesis OR at a known earlier seq when
  // the window is a tail of a longer file. We anchor on the FIRST record's own
  // declared prevHash, then verify each subsequent link + seq monotonicity.
  for (let i = 0; i < list.length; i++) {
    const env = list[i];
    if (!Number.isInteger(env.seq)) {
      return { ok: false, tampered: true, reason: 'malformed-seq', at: i };
    }
    if (i === 0) {
      expectedSeq = env.seq;
      prevHash = sealBox.recordHash(env);
      continue;
    }
    // seq must increase by exactly 1 (DUPLICATE → same/lower seq; REORDER → out of order)
    if (env.seq !== expectedSeq + 1) {
      return { ok: false, tampered: true, reason: 'reorder-or-drop', at: i };
    }
    // prevHash must link the prior record (MIDDLE-DROP / REORDER break this)
    if (env.prevHash !== prevHash) {
      return { ok: false, tampered: true, reason: 'reorder-or-drop', at: i };
    }
    expectedSeq = env.seq;
    prevHash = sealBox.recordHash(env);
  }
  return { ok: true, tampered: false, reason: null, at: null };
}

/**
 * Tail-truncation detection (LD-TAMPER-CHAIN): compare the returned set
 * against a client-persisted high-water-mark { maxSeq, count }. A server that
 * returns fewer/older records than the persisted HWM is flagged — the backward
 * chain alone cannot catch this (a shorter chain stays internally valid).
 *
 * @param {object[]} envelopes  the returned cbox-v1 envelopes (file order)
 * @param {{maxSeq:number,count:number}} hwm  last-seen seq + record count
 */
function verifyChainAgainstHwm(envelopes, hwm) {
  const chain = verifyChain(envelopes);
  if (!chain.ok) return chain;
  const list = Array.isArray(envelopes) ? envelopes.filter(e => e && e.seal === sealBox.SEAL_MARKER) : [];
  if (!hwm || typeof hwm.maxSeq !== 'number') return { ok: true, tampered: false, reason: null, at: null };
  const observedMaxSeq = list.length ? list[list.length - 1].seq : -1;
  const observedCount = list.length;
  if (observedMaxSeq < hwm.maxSeq || observedCount < (hwm.count || 0)) {
    return { ok: false, tampered: true, reason: 'tail-truncation', at: null };
  }
  return { ok: true, tampered: false, reason: null, at: null };
}

/**
 * Compute the HWM to persist after a clean read (for the next session's
 * tail-truncation check). The caller persists this (ideally signed) in
 * IndexedDB, monotonically (never rewind).
 */
function computeHwm(envelopes) {
  const list = Array.isArray(envelopes) ? envelopes.filter(e => e && e.seal === sealBox.SEAL_MARKER) : [];
  return { maxSeq: list.length ? list[list.length - 1].seq : -1, count: list.length };
}

module.exports = {
  recipientKexCanonicalBytes,
  signRecipientKex,
  verifyRecipientKex,
  verifyChain,
  verifyChainAgainstHwm,
  computeHwm,
};
